import * as React from 'react';
import { cn } from '@/lib/utils';
import { Stack, type StackGap } from '@/components/ui/stack';

/**
 * PageWithRail — страница «основная колонка + правая панель» (Earth Tech DS v1,
 * layout-слой).
 *
 * Правило спеки: обе колонки живут ВНУТРИ одного грида, а не в двух соседних
 * контейнерах со своими отступами. Только так первые карточки колонок
 * гарантированно начинаются на одной горизонтали, а ряды не «плывут»
 * (регрессия карточки поля 2026-07-29).
 *
 * - сетка `minmax(0,1fr) clamp(20rem,26vw,30rem)`, gutter `gap-6` (24px);
 * - ниже 1024 (`lg`) панель падает вниз одной колонкой;
 * - ширина панели адаптивная с капом 30rem: ИНВАРИАНТ — панель никогда не шире
 *   основной колонки на поддерживаемых viewport'ах ≥1024. Фиксированные 30rem
 *   этот инвариант ломали: на 1024–1119 (sidebar collapsed 72px + main p-6)
 *   основной колонке доставалось 385px против 480px панели — иерархия
 *   инвертировалась (ревью head-web #581). `clamp` вместо ступеней по
 *   медиа-запросам — чтобы не было скачка reflow на границе брейкпоинта;
 * - `minmax(0,·)` вместо голых `fr` — длинный контент не распирает колонку;
 * - обе колонки — `Stack` с единым вертикальным шагом (по умолчанию 24px),
 *   поэтому секциям НЕ нужны собственные `mt-*` / `mb-*` / `space-y-*`
 *   (это ловит статический гард `src/__tests__/layout-spacing.test.ts`);
 * - `items-start` — колонки не растягиваются друг под друга.
 *
 * ```tsx
 * <PageWithRail rail={<><Passport/><Weather/></>}>
 *   <MiniMap/>
 *   <SowingCard/>
 * </PageWithRail>
 * ```
 */
export interface PageWithRailProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Содержимое правой панели. Каждый элемент — секция/карточка. */
  rail: React.ReactNode;
  /** Содержимое основной колонки. */
  children: React.ReactNode;
  /** Вертикальный шаг внутри колонок. По умолчанию 6 (24px). */
  gap?: StackGap;
  /** Классы на основную колонку (например `min-w-0`-специфика экрана). */
  mainClassName?: string;
  /** Классы на колонку-панель. */
  railClassName?: string;
}

export const PageWithRail = React.forwardRef<HTMLDivElement, PageWithRailProps>(
  ({ className, rail, children, gap = 6, mainClassName, railClassName, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // max-w-screen-2xl + mx-auto: на ultra-wide основная колонка не
        // растягивается безразмерно, панель не прижимается к краю (AK-459).
        'mx-auto grid w-full max-w-screen-2xl grid-cols-1 gap-6 ' +
          'lg:grid-cols-[minmax(0,1fr)_clamp(20rem,26vw,30rem)] lg:items-start',
        className,
      )}
      {...props}
    >
      <Stack gap={gap} className={cn('min-w-0', mainClassName)}>
        {children}
      </Stack>
      <Stack gap={gap} className={cn('min-w-0', railClassName)}>
        {rail}
      </Stack>
    </div>
  ),
);
PageWithRail.displayName = 'PageWithRail';
